import { inject, Injectable } from '@angular/core';
import { AuthPayload } from './models/auth.payload';
import { environment } from '../../../../environments/environment';
import { TokenDto } from './models/token.dto';
import { MeService } from '../me/me.service';
import { Router } from '@angular/router';
import { Api } from '../api/api';
import { ApiResponse } from "../../types/interfaces/api-response.interface";
import { CreatePasswordPayload, ForgotPasswordPayload } from '../../types/payloads/auth.payload';
import { RETURN_URL_QUERY_PARAM } from '../../../../environments/shared-environment/config';
import { sanitizeReturnUrl } from '../../guard/guard';

@Injectable({
  providedIn: 'root',
})
export class AuthService {

  constructor(
    private readonly router: Router,
    private readonly meService: MeService,
  ) { }

  private readonly api: Api = new Api();

  public async login(payload: AuthPayload): Promise<void> {
    const response = await this.api.postAsync<ApiResponse<TokenDto>>(environment.routes.users.login, payload);
  
    if (!response?.data?.authToken)
      throw new Error(response?.message);
  
    this.meService.me = response.data.user!;
    this.saveToken(response!.data, payload.keepConnected!);
  }

  public async resetPassword(payload: CreatePasswordPayload): Promise<void> {
    const response = await this.api.postAsync<ApiResponse<TokenDto>>(environment.routes.users.resetPwd, payload);
  
    if (!response?.success)
      throw new Error(response?.message);
  }

  public async forgotPassword(payload: ForgotPasswordPayload): Promise<void> {
    const response = await this.api.postAsync<ApiResponse<TokenDto>>(environment.routes.users.forgotPwd, payload);
  
    if (!response?.success)
      throw new Error(response?.message);
  }
  
  public async logout(options?: { preserveReturnUrl?: boolean }): Promise<void> {
    localStorage.removeItem(environment.keys.token);
    sessionStorage.removeItem(environment.keys.token);

    const returnUrl = options?.preserveReturnUrl ? sanitizeReturnUrl(this.router.url) : null;
    const queryParams = returnUrl ? { [RETURN_URL_QUERY_PARAM]: returnUrl } : undefined;

    await this.router.navigate([environment.configs.unauthenticatedRoute], { queryParams });
  }
  
  public isLoggedIn(): boolean {
    return !!this.getToken();
  }
  
  public getToken(): string | null {
    const token = localStorage.getItem(environment.keys.token) || sessionStorage.getItem(environment.keys.token);
    return token ? JSON.parse(token) : null;
  }
  
  private saveToken(token: TokenDto, keepConnected: boolean): void {
    const storage = keepConnected ? localStorage : sessionStorage;
    storage.setItem(environment.keys.token, JSON.stringify(token.authToken));
  }  
  
}
